import { useEffect, useRef, useState } from 'react'
import { IconTrash } from './ScanIcons'
import {
  SUGGESTION_AXIS_LOCK_PX,
  SUGGESTION_REVEAL_WIDTH_PX,
  suggestionGestureIntent,
  suggestionRevealOffset,
  suggestionShouldStayOpen,
} from '../../utils/scanSuggestionGesture'
import { isUsableProductName, sourceKindLabel } from '../../utils/productName'

function candidateImage(candidate, fallback) {
  return candidate.official_image_url
    || candidate.image
    || candidate.officialImage?.url
    || candidate.officialImage
    || fallback
    || ''
}

function suggestionKey(list) {
  return (Array.isArray(list) ? list : []).map((item) => item.id).join('|')
}

export default function ScanSuggestionList({
  suggestions = [],
  previewUrl = '',
  onSelect,
  onReject,
}) {
  const [openId, setOpenId] = useState(null)
  const [offset, setOffset] = useState(0)
  const [draggingId, setDraggingId] = useState('')
  const [leavingId, setLeavingId] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [liveText, setLiveText] = useState('')
  const originRef = useRef(null)
  const suppressClickRef = useRef(false)
  const intentRef = useRef('idle')
  const dragIdRef = useRef('')
  const offsetRef = useRef(0)

  const resetTabs = () => {
    setOpenId(null)
    offsetRef.current = 0
    setOffset(0)
    setDraggingId('')
    dragIdRef.current = ''
    originRef.current = null
    suppressClickRef.current = false
    intentRef.current = 'idle'
  }

  useEffect(() => {
    resetTabs()
  }, [suggestionKey(suggestions)])

  useEffect(() => {
    const close = (event) => {
      if (event.target?.closest?.('.suggestion-swipe-row')) return
      resetTabs()
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [])

  const reject = (candidate) => {
    setLeavingId(candidate.id)
    setLiveText(`${candidate.name_fr} écarté`)
    window.setTimeout(() => {
      onReject?.(candidate)
      resetTabs()
      setLeavingId('')
    }, 180)
  }

  const select = (candidate) => {
    setSelectedId(candidate.id)
    resetTabs()
    setLiveText(`${candidate.name_fr} sélectionné`)
    onSelect?.(candidate)
    window.setTimeout(() => setSelectedId(''), 420)
  }

  return (
    <div className="scan-suggestions">
      <p className="visually-hidden" aria-live="polite">{liveText}</p>
      {suggestions.filter((candidate) => isUsableProductName(candidate.name_fr)).map((candidate) => {
        const open = openId === candidate.id
        const dragging = draggingId === candidate.id
        const shift = dragging ? offset : (open ? -SUGGESTION_REVEAL_WIDTH_PX : 0)
        return (
          <div
            key={candidate.id}
            className={`suggestion-swipe-row${leavingId === candidate.id ? ' is-leaving' : ''}${selectedId === candidate.id ? ' is-selected' : ''}`}
          >
            <button
              type="button"
              className="suggestion-delete-action"
              tabIndex={open ? 0 : -1}
              aria-hidden={!open}
              aria-label="Supprimer cette suggestion"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation()
                reject(candidate)
              }}
            >
              <IconTrash />
              <span>Supprimer</span>
            </button>
            <article
              className="suggestion-card scan-suggestion-card"
              role="button"
              tabIndex={0}
              aria-label={`Sélectionner ${candidate.name_fr}`}
              style={{ transform: `translateX(${shift}px)` }}
              onPointerDown={(event) => {
                if (event.pointerType === 'mouse' && event.button !== 0) return
                originRef.current = { x: event.clientX, y: event.clientY }
                suppressClickRef.current = false
                intentRef.current = 'idle'
                dragIdRef.current = candidate.id
                setDraggingId(candidate.id)
                if (openId && openId !== candidate.id) {
                  setOpenId(null)
                  offsetRef.current = 0
                  setOffset(0)
                }
              }}
              onPointerMove={(event) => {
                if (!originRef.current || dragIdRef.current !== candidate.id) return
                const point = { x: event.clientX, y: event.clientY }
                const intent = suggestionGestureIntent(originRef.current, point, SUGGESTION_AXIS_LOCK_PX)
                if (intent === 'tap' || intent === 'idle') return
                if (intent === 'scroll') {
                  intentRef.current = 'scroll'
                  return
                }
                intentRef.current = intent
                suppressClickRef.current = true
                event.currentTarget.setPointerCapture?.(event.pointerId)
                event.preventDefault()
                const dx = point.x - originRef.current.x
                const nextOffset = suggestionRevealOffset(dx, open)
                offsetRef.current = nextOffset
                setOffset(nextOffset)
              }}
              onPointerUp={() => {
                if (dragIdRef.current !== candidate.id) return
                const stay = suggestionShouldStayOpen(offsetRef.current)
                const nextOffset = stay ? -SUGGESTION_REVEAL_WIDTH_PX : 0
                offsetRef.current = nextOffset
                setOpenId(stay ? candidate.id : null)
                setOffset(nextOffset)
                setDraggingId('')
                dragIdRef.current = ''
                originRef.current = null
              }}
              onPointerCancel={() => {
                setDraggingId('')
                dragIdRef.current = ''
                const nextOffset = open ? -SUGGESTION_REVEAL_WIDTH_PX : 0
                offsetRef.current = nextOffset
                setOffset(nextOffset)
                originRef.current = null
              }}
              onClick={() => {
                if (suppressClickRef.current || intentRef.current.startsWith('swipe')) {
                  suppressClickRef.current = false
                  return
                }
                if (open) {
                  setOpenId(null)
                  offsetRef.current = 0
                  setOffset(0)
                  return
                }
                select(candidate)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  select(candidate)
                }
                if (event.key === 'Delete' || event.key === 'Backspace') {
                  event.preventDefault()
                  reject(candidate)
                }
              }}
              onContextMenu={(event) => {
                event.preventDefault()
                setOpenId(candidate.id)
                offsetRef.current = -SUGGESTION_REVEAL_WIDTH_PX
                setOffset(-SUGGESTION_REVEAL_WIDTH_PX)
              }}
            >
              {candidateImage(candidate, previewUrl) ? (
                <img src={candidateImage(candidate, previewUrl)} alt="" />
              ) : (
                <div className="scan-suggestion-placeholder" aria-hidden="true">?</div>
              )}
              <div>
                <strong>{candidate.name_fr}</strong>
                <small>{[candidate.generation, candidate.variant_fr || candidate.variant].filter(Boolean).join(' · ')}</small>
                <small>
                  {sourceKindLabel(candidate.source_kind)}
                </small>
              </div>
            </article>
          </div>
        )
      })}
    </div>
  )
}
